import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { ImagePlus, Upload, Trash2, Globe, Lock, MapPin, List, Plus, X, Star, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { normalizeUseCase, normalizeInfo, isInfoActive, useSiteSettings } from '../context/SiteSettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SiteImagesManager({ spaces, getHeaders, onSpacesChanged }) {
  const [siteSettings, setSiteSettings] = useState({ logo_url: '', hero_url: '', auth_bg_url: '', use_cases: [] });
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/site-settings`);
        setSiteSettings(res.data);
      } catch { /* ignore */ }
    };
    fetchSettings();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget.key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      const imageUrl = res.data.image_url;

      if (uploadTarget.type === 'site') {
        await axios.put(`${API}/admin/site-settings`, { [uploadTarget.key]: imageUrl }, {
          headers: { ...getHeaders(), 'Content-Type': 'application/json' },
          withCredentials: true
        });
        setSiteSettings(prev => ({ ...prev, [uploadTarget.key]: imageUrl }));
        toast.success('Image du site mise a jour');
      } else if (uploadTarget.type === 'space') {
        await axios.put(`${API}/admin/spaces/${uploadTarget.key}`, { image_url: imageUrl }, {
          headers: { ...getHeaders(), 'Content-Type': 'application/json' },
          withCredentials: true
        });
        toast.success('Photo de l\'espace mise a jour');
        if (onSpacesChanged) onSpacesChanged();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(null);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (type, key) => {
    setUploadTarget({ type, key });
    fileInputRef.current?.click();
  };

  const removeSiteImage = async (key) => {
    try {
      await axios.put(`${API}/admin/site-settings`, { [key]: '' }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      setSiteSettings(prev => ({ ...prev, [key]: '' }));
      toast.success('Image supprimee');
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const removeSpaceImage = async (spaceId) => {
    try {
      await axios.put(`${API}/admin/spaces/${spaceId}`, { image_url: '' }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      toast.success('Photo supprimee');
      if (onSpacesChanged) onSpacesChanged();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const siteImages = [
    { key: 'logo_url', label: 'Logo du site', desc: 'Affiche dans la barre de navigation et le footer', icon: Globe, current: siteSettings.logo_url },
    { key: 'hero_url', label: 'Photo d\'accueil (Hero)', desc: 'Image principale de la page d\'accueil', icon: Globe, current: siteSettings.hero_url },
    { key: 'auth_bg_url', label: 'Photo page connexion', desc: 'Image de fond de la page de connexion/inscription', icon: Lock, current: siteSettings.auth_bg_url },
  ];

  return (
    <div className="space-y-8" data-testid="site-images-manager">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />

      {/* Theme switcher — try different color palettes site-wide */}
      <ThemePickerCard
        currentTheme={siteSettings.theme || 'default'}
        getHeaders={getHeaders}
        onSaved={(val) => setSiteSettings((prev) => ({ ...prev, theme: val }))}
      />

      {/* Payment disclaimer message — shown to clients in a popup right before payment */}
      <PaymentDisclaimerCard
        value={siteSettings.payment_disclaimer || ''}
        getHeaders={getHeaders}
        onSaved={(val) => setSiteSettings((prev) => ({ ...prev, payment_disclaimer: val }))}
      />

      {/* Site images */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#C77D63]" />
          Images du site
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {siteImages.map(img => (
            <div key={img.key} className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden" data-testid={`site-img-${img.key}`}>
              <div className="h-40 bg-[#F0EFEA] flex items-center justify-center relative">
                {img.current ? (
                  <img src={img.current} alt={img.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <ImagePlus className="w-8 h-8 text-[#E2E2DF] mx-auto mb-1" />
                    <p className="text-[10px] text-[#737373]">Aucune image</p>
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-[#1A3C34]">{img.label}</p>
                <p className="text-[10px] text-[#737373] mt-0.5">{img.desc}</p>
                <div className="flex gap-1.5 mt-2">
                  <Button
                    size="sm"
                    className="flex-1 h-7 text-[10px] bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
                    onClick={() => triggerUpload('site', img.key)}
                    disabled={uploading === img.key}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploading === img.key ? 'Upload...' : img.current ? 'Changer' : 'Ajouter'}
                  </Button>
                  {img.current && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[10px] border-red-200 text-red-500 hover:bg-red-50"
                      onClick={() => removeSiteImage(img.key)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Space images */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#C77D63]" />
          Photos des espaces
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map(space => (
            <div key={space.space_id} className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden" data-testid={`space-img-${space.space_id}`}>
              <div className="h-36 bg-[#F0EFEA] flex items-center justify-center relative">
                {space.image_url ? (
                  <img src={space.image_url} alt={space.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <ImagePlus className="w-8 h-8 text-[#E2E2DF] mx-auto mb-1" />
                    <p className="text-[10px] text-[#737373]">Aucune photo</p>
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-[#1A3C34]">{space.name}</p>
                <p className="text-[10px] text-[#737373]">{space.capacity} pers. | {space.price_hourly} EUR/h</p>
                <div className="flex gap-1.5 mt-2">
                  <Button
                    size="sm"
                    className="flex-1 h-7 text-[10px] bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
                    onClick={() => triggerUpload('space', space.space_id)}
                    disabled={uploading === space.space_id}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploading === space.space_id ? 'Upload...' : space.image_url ? 'Changer' : 'Ajouter'}
                  </Button>
                  {space.image_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[10px] border-red-200 text-red-500 hover:bg-red-50"
                      onClick={() => removeSpaceImage(space.space_id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Use cases editor */}
      <UseCasesEditor siteSettings={siteSettings} setSiteSettings={setSiteSettings} getHeaders={getHeaders} />

      {/* Highlights editor (strong points shown below hero) */}
      <HighlightsEditor siteSettings={siteSettings} setSiteSettings={setSiteSettings} getHeaders={getHeaders} />

      {/* Important infos / announcements */}
      <InfosEditor siteSettings={siteSettings} setSiteSettings={setSiteSettings} getHeaders={getHeaders} />
    </div>
  );
}

function UseCasesEditor({ siteSettings, setSiteSettings, getHeaders }) {
  const { refresh } = useSiteSettings();
  const defaultItems = [
    { fr: "Pour une formation", nl: "Voor een opleiding", en: "For a training" },
    { fr: "Pour une seance photo", nl: "Voor een fotoshoot", en: "For a photo shoot" },
    { fr: "Pour une reunion de syndic", nl: "Voor een syndicusvergadering", en: "For a condo meeting" },
    { fr: "Pour un atelier de reparation", nl: "Voor een reparatieworkshop", en: "For a repair workshop" },
    { fr: "Pour une presentation de produit", nl: "Voor een productpresentatie", en: "For a product launch" },
    { fr: "Pour une seance de degustation", nl: "Voor een degustatiesessie", en: "For a tasting session" },
    { fr: "Pour un club d'investissement", nl: "Voor een investeringsclub", en: "For an investment club" },
    { fr: "Pour un cours de yoga", nl: "Voor een yogales", en: "For a yoga class" },
    { fr: "Pour un coaching individuel", nl: "Voor een individuele coaching", en: "For one-on-one coaching" },
    { fr: "Pour un afterwork networking", nl: "Voor een afterwork-netwerkevent", en: "For an afterwork networking" },
  ];
  const [items, setItems] = useState(
    siteSettings.use_cases?.length > 0
      ? siteSettings.use_cases.map(normalizeUseCase)
      : defaultItems
  );
  const [newItem, setNewItem] = useState({ fr: '', nl: '', en: '' });
  const [speed1, setSpeed1] = useState(siteSettings.marquee_speed_1 || 35);
  const [speed2, setSpeed2] = useState(siteSettings.marquee_speed_2 || 28);
  const [dir1, setDir1] = useState(siteSettings.marquee_dir_1 || 'ltr');
  const [dir2, setDir2] = useState(siteSettings.marquee_dir_2 || 'ltr');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (siteSettings.use_cases?.length > 0) {
      setItems(siteSettings.use_cases.map(normalizeUseCase));
    }
    if (siteSettings.marquee_speed_1) setSpeed1(siteSettings.marquee_speed_1);
    if (siteSettings.marquee_speed_2) setSpeed2(siteSettings.marquee_speed_2);
    if (siteSettings.marquee_dir_1) setDir1(siteSettings.marquee_dir_1);
    if (siteSettings.marquee_dir_2) setDir2(siteSettings.marquee_dir_2);
  }, [siteSettings.use_cases, siteSettings.marquee_speed_1, siteSettings.marquee_speed_2, siteSettings.marquee_dir_1, siteSettings.marquee_dir_2]);

  const addItem = () => {
    if (!newItem.fr.trim()) return;
    setItems(prev => [...prev, {
      fr: newItem.fr.trim(),
      nl: newItem.nl.trim(),
      en: newItem.en.trim(),
    }]);
    setNewItem({ fr: '', nl: '', en: '' });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItemLang = (index, lang, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [lang]: value } : it));
  };

  const moveItem = (index, direction) => {
    setItems(prev => {
      const arr = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return arr;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const saveUseCases = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, { use_cases: items, marquee_speed_1: speed1, marquee_speed_2: speed2, marquee_dir_1: dir1, marquee_dir_2: dir2 }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      setSiteSettings(prev => ({ ...prev, use_cases: items, marquee_speed_1: speed1, marquee_speed_2: speed2, marquee_dir_1: dir1, marquee_dir_2: dir2 }));
      await refresh();
      toast.success('Liste mise a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="use-cases-editor">
      <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
        <List className="w-5 h-5 text-[#C77D63]" />
        Liste "Ideal pour" (page d'accueil) - FR / NL / EN
      </h3>
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <p className="text-xs text-[#737373] mb-4">
          Chaque element accepte 3 traductions (FR, NL, EN). Le marquee affichera la langue selectionnee par le visiteur (fallback FR).
        </p>
        <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto">
          {items.map((item, i) => (
            <div key={i} className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-lg p-2.5 group" data-testid={`use-case-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 text-center text-[10px] text-[#737373]">{i + 1}</span>
                <span className="flex-1 text-xs text-[#737373]">Element {i + 1}</span>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => moveItem(i, -1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Monter" disabled={i === 0}>▲</button>
                  <button onClick={() => moveItem(i, 1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Descendre" disabled={i === items.length - 1}>▼</button>
                  <button onClick={() => removeItem(i)} className="text-[#737373] hover:text-red-500 p-1" title="Supprimer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">FR</label>
                  <input
                    data-testid={`use-case-${i}-fr`}
                    value={item.fr}
                    onChange={e => updateItemLang(i, 'fr', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">NL</label>
                  <input
                    data-testid={`use-case-${i}-nl`}
                    value={item.nl}
                    onChange={e => updateItemLang(i, 'nl', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                    placeholder="(fallback FR si vide)"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">EN</label>
                  <input
                    data-testid={`use-case-${i}-en`}
                    value={item.en}
                    onChange={e => updateItemLang(i, 'en', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                    placeholder="(fallback FR si vide)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F0EFEA] border border-[#E2E2DF] rounded-lg p-3 mb-3">
          <p className="text-[11px] font-semibold text-[#1A3C34] mb-2">Ajouter un element</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              value={newItem.fr}
              onChange={e => setNewItem(prev => ({ ...prev, fr: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="FR - Ex: Pour un team building"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-use-case-fr"
            />
            <input
              value={newItem.nl}
              onChange={e => setNewItem(prev => ({ ...prev, nl: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="NL - Ex: Voor een teambuilding"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-use-case-nl"
            />
            <input
              value={newItem.en}
              onChange={e => setNewItem(prev => ({ ...prev, en: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="EN - Ex: For a team building"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-use-case-en"
            />
          </div>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={addItem} disabled={!newItem.fr.trim()} className="h-8 px-3 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-lg text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-[#E2E2DF] space-y-3">
          <p className="text-xs font-semibold text-[#1A3C34]">Vitesse et direction de defilement</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] text-[#737373]">Bandeau 1 : {speed1}s (plus bas = plus rapide)</label>
              <input
                type="range"
                min="3"
                max="80"
                value={speed1}
                onChange={e => setSpeed1(parseInt(e.target.value))}
                className="w-full h-1.5 bg-[#E2E2DF] rounded-lg appearance-none cursor-pointer accent-[#C77D63]"
                data-testid="speed-slider-1"
              />
              <div className="flex items-center gap-3 text-[11px] text-[#1A3C34]">
                <span className="text-[#737373]">Sens :</span>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="dir1" value="ltr" checked={dir1 === 'ltr'} onChange={() => setDir1('ltr')} className="accent-[#C77D63]" data-testid="dir1-ltr" />
                  <span>Gauche → Droite</span>
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="dir1" value="rtl" checked={dir1 === 'rtl'} onChange={() => setDir1('rtl')} className="accent-[#C77D63]" data-testid="dir1-rtl" />
                  <span>Droite → Gauche</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-[#737373]">Bandeau 2 : {speed2}s (plus bas = plus rapide)</label>
              <input
                type="range"
                min="3"
                max="80"
                value={speed2}
                onChange={e => setSpeed2(parseInt(e.target.value))}
                className="w-full h-1.5 bg-[#E2E2DF] rounded-lg appearance-none cursor-pointer accent-[#C77D63]"
                data-testid="speed-slider-2"
              />
              <div className="flex items-center gap-3 text-[11px] text-[#1A3C34]">
                <span className="text-[#737373]">Sens :</span>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="dir2" value="ltr" checked={dir2 === 'ltr'} onChange={() => setDir2('ltr')} className="accent-[#C77D63]" data-testid="dir2-ltr" />
                  <span>Gauche → Droite</span>
                </label>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="dir2" value="rtl" checked={dir2 === 'rtl'} onChange={() => setDir2('rtl')} className="accent-[#C77D63]" data-testid="dir2-rtl" />
                  <span>Droite → Gauche</span>
                </label>
              </div>
            </div>
          </div>
          <Button size="sm" onClick={saveUseCases} disabled={saving} className="bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg h-9 px-6 text-sm font-bold" data-testid="save-use-cases-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder la liste, vitesse et sens'}
          </Button>
        </div>
      </div>
    </div>
  );
}


function HighlightsEditor({ siteSettings, setSiteSettings, getHeaders }) {
  const { refresh } = useSiteSettings();
  const defaultItems = [
    { fr: "Acces facile en voiture", nl: "Vlot bereikbaar met de auto", en: "Easy access by car" },
    { fr: "Parking disque bleu jusqu'a 18h", nl: "Blauwe zone tot 18u", en: "Blue-zone parking until 6 pm" },
    { fr: "Terminus des bus 28 et 36 a 2 minutes", nl: "Eindhalte bus 28 en 36 op 2 min", en: "Bus 28 & 36 terminus 2 min away" },
    { fr: "Acces 7j/7 par code personnel", nl: "Toegang 7/7 met persoonlijke code", en: "7/7 access by personal code" },
    { fr: "Cafe, the et eau a volonte", nl: "Koffie, thee en water onbeperkt", en: "Free coffee, tea & water" },
  ];
  const defaultTitle = {
    fr: "Pourquoi choisir Flexi-location ?",
    nl: "Waarom kiezen voor Flexi-location?",
    en: "Why choose Flexi-location?",
  };
  const [items, setItems] = useState(
    siteSettings.highlights?.length > 0
      ? siteSettings.highlights.map(normalizeUseCase)
      : defaultItems
  );
  const [title, setTitle] = useState(
    siteSettings.highlights_title
      ? normalizeUseCase(siteSettings.highlights_title)
      : defaultTitle
  );
  const [newItem, setNewItem] = useState({ fr: '', nl: '', en: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (siteSettings.highlights?.length > 0) {
      setItems(siteSettings.highlights.map(normalizeUseCase));
    }
    if (siteSettings.highlights_title) {
      setTitle(normalizeUseCase(siteSettings.highlights_title));
    }
  }, [siteSettings.highlights, siteSettings.highlights_title]);

  const addItem = () => {
    if (!newItem.fr.trim()) return;
    setItems(prev => [...prev, {
      fr: newItem.fr.trim(),
      nl: newItem.nl.trim(),
      en: newItem.en.trim(),
    }]);
    setNewItem({ fr: '', nl: '', en: '' });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItemLang = (index, lang, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [lang]: value } : it));
  };

  const moveItem = (index, direction) => {
    setItems(prev => {
      const arr = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return arr;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const saveHighlights = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        highlights: items,
        highlights_title: title,
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      setSiteSettings(prev => ({ ...prev, highlights: items, highlights_title: title }));
      await refresh();
      toast.success('Points forts mis a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="highlights-editor">
      <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
        <Star className="w-5 h-5 text-[#C77D63]" />
        Points forts du lieu (page d'accueil sous le hero) - FR / NL / EN
      </h3>
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <p className="text-xs text-[#737373] mb-4">
          Liste a puces affichee juste sous le hero. Chaque element accepte 3 traductions (FR, NL, EN).
          Exemples : "Parking disque bleu jusqu'a 18h", "Terminus bus 28 et 36 a 2 min", "Acces facile".
        </p>

        {/* Title (FR/NL/EN) */}
        <div className="bg-[#F0EFEA] border border-[#E2E2DF] rounded-lg p-3 mb-4">
          <p className="text-[11px] font-semibold text-[#1A3C34] mb-2">Titre de la section</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              data-testid="highlights-title-fr"
              value={title.fr}
              onChange={e => setTitle(prev => ({ ...prev, fr: e.target.value }))}
              placeholder="FR - Ex: Pourquoi choisir Flexi-location ?"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
            <input
              data-testid="highlights-title-nl"
              value={title.nl}
              onChange={e => setTitle(prev => ({ ...prev, nl: e.target.value }))}
              placeholder="NL"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
            <input
              data-testid="highlights-title-en"
              value={title.en}
              onChange={e => setTitle(prev => ({ ...prev, en: e.target.value }))}
              placeholder="EN"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
          </div>
        </div>

        {/* Items list */}
        <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto">
          {items.map((item, i) => (
            <div key={i} className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-lg p-2.5 group" data-testid={`highlight-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 text-center text-[10px] text-[#737373]">{i + 1}</span>
                <span className="flex-1 text-xs text-[#737373]">Point fort {i + 1}</span>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => moveItem(i, -1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Monter" disabled={i === 0}>▲</button>
                  <button onClick={() => moveItem(i, 1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Descendre" disabled={i === items.length - 1}>▼</button>
                  <button onClick={() => removeItem(i)} className="text-[#737373] hover:text-red-500 p-1" title="Supprimer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">FR</label>
                  <input
                    data-testid={`highlight-${i}-fr`}
                    value={item.fr}
                    onChange={e => updateItemLang(i, 'fr', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">NL</label>
                  <input
                    data-testid={`highlight-${i}-nl`}
                    value={item.nl}
                    onChange={e => updateItemLang(i, 'nl', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                    placeholder="(fallback FR si vide)"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#737373] uppercase tracking-wide">EN</label>
                  <input
                    data-testid={`highlight-${i}-en`}
                    value={item.en}
                    onChange={e => updateItemLang(i, 'en', e.target.value)}
                    className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                    placeholder="(fallback FR si vide)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add new item */}
        <div className="bg-[#F0EFEA] border border-[#E2E2DF] rounded-lg p-3 mb-3">
          <p className="text-[11px] font-semibold text-[#1A3C34] mb-2">Ajouter un point fort</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              value={newItem.fr}
              onChange={e => setNewItem(prev => ({ ...prev, fr: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="FR - Ex: Climatisation reglable"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-highlight-fr"
            />
            <input
              value={newItem.nl}
              onChange={e => setNewItem(prev => ({ ...prev, nl: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="NL"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-highlight-nl"
            />
            <input
              value={newItem.en}
              onChange={e => setNewItem(prev => ({ ...prev, en: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="EN"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-highlight-en"
            />
          </div>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={addItem} disabled={!newItem.fr.trim()} className="h-8 px-3 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-lg text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-[#E2E2DF]">
          <Button size="sm" onClick={saveHighlights} disabled={saving} className="bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg h-9 px-6 text-sm font-bold" data-testid="save-highlights-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder les points forts'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InfosEditor({ siteSettings, setSiteSettings, getHeaders }) {
  const { refresh } = useSiteSettings();
  const defaultItems = [
    {
      fr: "Tarifs d'ouverture - susceptibles d'evoluer dans les prochains mois.",
      nl: "Openingstarieven - kunnen de komende maanden evolueren.",
      en: "Opening prices - subject to change in the coming months.",
      expires_at: "",
    },
  ];
  const defaultTitle = {
    fr: "Infos importantes",
    nl: "Belangrijke info",
    en: "Important info",
  };
  const [items, setItems] = useState(
    siteSettings.infos?.length > 0
      ? siteSettings.infos.map(normalizeInfo)
      : defaultItems
  );
  const [title, setTitle] = useState(
    siteSettings.infos_title
      ? normalizeUseCase(siteSettings.infos_title)
      : defaultTitle
  );
  const [newItem, setNewItem] = useState({ fr: '', nl: '', en: '', expires_at: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (siteSettings.infos?.length > 0) {
      setItems(siteSettings.infos.map(normalizeInfo));
    }
    if (siteSettings.infos_title) {
      setTitle(normalizeUseCase(siteSettings.infos_title));
    }
  }, [siteSettings.infos, siteSettings.infos_title]);

  const addItem = () => {
    if (!newItem.fr.trim()) return;
    setItems(prev => [...prev, {
      fr: newItem.fr.trim(),
      nl: newItem.nl.trim(),
      en: newItem.en.trim(),
      expires_at: newItem.expires_at || '',
    }]);
    setNewItem({ fr: '', nl: '', en: '', expires_at: '' });
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItemField = (index, field, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };

  const moveItem = (index, direction) => {
    setItems(prev => {
      const arr = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return arr;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const saveInfos = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        infos: items,
        infos_title: title,
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });
      setSiteSettings(prev => ({ ...prev, infos: items, infos_title: title }));
      await refresh();
      toast.success('Infos importantes mises a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    if (!window.confirm('Vider la section "Infos importantes" ? Elle disparaitra de la page d\'accueil.')) return;
    setItems([]);
  };

  const purgeExpired = () => {
    const expired = items.filter(it => !isInfoActive(it));
    if (expired.length === 0) {
      toast.info('Aucune info expiree a supprimer');
      return;
    }
    if (!window.confirm(`Supprimer ${expired.length} info(s) expiree(s) ?`)) return;
    setItems(items.filter(isInfoActive));
  };

  return (
    <div data-testid="infos-editor">
      <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
        <Info className="w-5 h-5 text-[#C77D63]" />
        Infos importantes (page d'accueil) - FR / NL / EN
      </h3>
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <p className="text-xs text-[#737373] mb-4">
          Bandeau d'annonces affiche sous les points forts. Utile pour signaler des tarifs d'ouverture susceptibles d'evoluer,
          une fermeture pour travaux, des conges, etc. Une <strong>date d'expiration optionnelle</strong> peut etre definie : l'info disparait automatiquement de la page d'accueil apres cette date (mais reste ici pour reutilisation). Si la liste est vide, la section ne s'affiche pas.
        </p>

        {/* Title (FR/NL/EN) */}
        <div className="bg-[#F0EFEA] border border-[#E2E2DF] rounded-lg p-3 mb-4">
          <p className="text-[11px] font-semibold text-[#1A3C34] mb-2">Titre du bandeau</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              data-testid="infos-title-fr"
              value={title.fr}
              onChange={e => setTitle(prev => ({ ...prev, fr: e.target.value }))}
              placeholder="FR - Ex: Infos importantes"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
            <input
              data-testid="infos-title-nl"
              value={title.nl}
              onChange={e => setTitle(prev => ({ ...prev, nl: e.target.value }))}
              placeholder="NL"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
            <input
              data-testid="infos-title-en"
              value={title.en}
              onChange={e => setTitle(prev => ({ ...prev, en: e.target.value }))}
              placeholder="EN"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
            />
          </div>
        </div>

        {/* Items list */}
        <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <p className="text-xs italic text-[#737373] py-4 text-center">
              Aucune info active. La section sera masquee sur la page d'accueil.
            </p>
          )}
          {items.map((item, i) => {
            const expired = !isInfoActive(item);
            return (
              <div key={i} className={`bg-[#F9F9F7] border rounded-lg p-2.5 group ${expired ? 'border-red-200 opacity-60' : 'border-[#E2E2DF]'}`} data-testid={`info-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 text-center text-[10px] text-[#737373]">{i + 1}</span>
                  <span className="flex-1 text-xs text-[#737373]">
                    Info {i + 1}
                    {expired && <span className="ml-2 text-red-500 font-semibold">(expiree - non affichee)</span>}
                  </span>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveItem(i, -1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Monter" disabled={i === 0}>▲</button>
                    <button onClick={() => moveItem(i, 1)} className="text-[#737373] hover:text-[#1A3C34] p-1 text-xs" title="Descendre" disabled={i === items.length - 1}>▼</button>
                    <button onClick={() => removeItem(i)} className="text-[#737373] hover:text-red-500 p-1" title="Supprimer">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-[10px] text-[#737373] uppercase tracking-wide">FR</label>
                    <textarea
                      data-testid={`info-${i}-fr`}
                      value={item.fr}
                      onChange={e => updateItemField(i, 'fr', e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34] resize-y"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#737373] uppercase tracking-wide">NL</label>
                    <textarea
                      data-testid={`info-${i}-nl`}
                      value={item.nl}
                      onChange={e => updateItemField(i, 'nl', e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34] resize-y"
                      placeholder="(fallback FR si vide)"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#737373] uppercase tracking-wide">EN</label>
                    <textarea
                      data-testid={`info-${i}-en`}
                      value={item.en}
                      onChange={e => updateItemField(i, 'en', e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34] resize-y"
                      placeholder="(fallback FR si vide)"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-dashed border-[#E2E2DF]">
                  <div>
                    <label className="text-[10px] text-[#737373] uppercase tracking-wide block">Expire le (optionnel)</label>
                    <input
                      type="date"
                      data-testid={`info-${i}-expires`}
                      value={item.expires_at || ''}
                      onChange={e => updateItemField(i, 'expires_at', e.target.value)}
                      className="bg-white border border-[#E2E2DF] rounded px-2 py-1 text-sm text-[#1A3C34]"
                    />
                  </div>
                  {item.expires_at && (
                    <button
                      type="button"
                      onClick={() => updateItemField(i, 'expires_at', '')}
                      className="text-[11px] text-[#737373] hover:text-red-500 underline pb-1"
                      title="Retirer la date d'expiration"
                    >
                      retirer
                    </button>
                  )}
                  <p className="text-[10px] text-[#737373] pb-1 italic ml-auto">
                    {item.expires_at
                      ? (expired ? 'Expiree, non affichee sur le site.' : `Affichee jusqu'au ${item.expires_at} inclus.`)
                      : 'Pas de date - affichee jusqu\'a suppression manuelle.'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add new item */}
        <div className="bg-[#F0EFEA] border border-[#E2E2DF] rounded-lg p-3 mb-3">
          <p className="text-[11px] font-semibold text-[#1A3C34] mb-2">Ajouter une info</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <input
              value={newItem.fr}
              onChange={e => setNewItem(prev => ({ ...prev, fr: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="FR - Ex: Site ferme du 15 au 30 juillet pour travaux"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-info-fr"
            />
            <input
              value={newItem.nl}
              onChange={e => setNewItem(prev => ({ ...prev, nl: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="NL"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-info-nl"
            />
            <input
              value={newItem.en}
              onChange={e => setNewItem(prev => ({ ...prev, en: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="EN"
              className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
              data-testid="new-info-en"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] text-[#737373] uppercase tracking-wide block">Expire le (optionnel)</label>
              <input
                type="date"
                value={newItem.expires_at}
                onChange={e => setNewItem(prev => ({ ...prev, expires_at: e.target.value }))}
                className="h-9 px-3 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#C77D63] bg-white"
                data-testid="new-info-expires"
              />
            </div>
            <Button size="sm" onClick={addItem} disabled={!newItem.fr.trim()} className="h-9 px-3 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-lg text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-[#E2E2DF] flex flex-wrap gap-2">
          <Button size="sm" onClick={saveInfos} disabled={saving} className="bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg h-9 px-6 text-sm font-bold" data-testid="save-infos-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder les infos'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={purgeExpired}
            className="rounded-lg h-9 px-4 text-sm border-[#E2E2DF] text-[#737373] hover:bg-[#F0EFEA]"
            data-testid="purge-expired-btn"
          >
            Purger les expirees
          </Button>
          {items.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={clearAll}
              className="rounded-lg h-9 px-4 text-sm border-red-200 text-red-500 hover:bg-red-50"
              data-testid="clear-infos-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Vider la section
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}



/* ------------------------------------------------------------------ */
/* Payment disclaimer (house rules popup before checkout)             */
/* ------------------------------------------------------------------ */
const DISCLAIMER_DEFAULT = `En confirmant le paiement, je m'engage à :

• Rendre les locaux dans l'état initial où je les ai trouvés.
• Ranger meubles, chaises et matériel comme à mon arrivée.
• Laisser les toilettes propres et fonctionnelles.
• Sortir mes déchets ou les déposer dans les poubelles prévues.

⚠️ En cas de désordre, salissure ou rangement non effectué, un supplément forfaitaire de 30 EUR (nettoyage et remise en état) sera facturé automatiquement.`;

// =============================================================================
// THEME PICKER — admin can swap the site's color palette in 1 click. The actual
// CSS overrides live in /src/themes.css; this component just persists the
// chosen theme key into site_settings.theme.
// =============================================================================
const THEMES = [
  {
    key: 'default',
    label: 'Original (vert sapin + terracotta)',
    swatches: ['#1A3C34', '#C77D63', '#F0EFEA', '#F9F9F7'],
  },
  {
    key: 'fresh-modern',
    label: 'Frais & moderne (bleu marine + corail)',
    swatches: ['#1e3a8a', '#fb7185', '#EFF6FF', '#F8FAFC'],
  },
  {
    key: 'warm-craft',
    label: 'Chaleureux & artisanal (vert mousse + ocre)',
    swatches: ['#3F4F2C', '#D4A04A', '#F2EAD6', '#FAF6EE'],
  },
  {
    key: 'elegant-premium',
    label: 'Élégant & premium (charbon + or)',
    swatches: ['#1F1F1F', '#B58D3E', '#F2F0E9', '#FAFAF8'],
  },
  {
    key: 'nordic-minimal',
    label: 'Minimaliste nordique (bleu glacier + rouge)',
    swatches: ['#2C5364', '#DD3F3F', '#EAF0F2', '#FBFBFA'],
  },
  {
    key: 'energetic',
    label: 'Énergique (bleu électrique + moutarde)',
    swatches: ['#1D4ED8', '#EAB308', '#FEF3C7', '#FAFAFA'],
  },
];

function ThemePickerCard({ currentTheme, getHeaders, onSaved }) {
  const [saving, setSaving] = React.useState(null);
  const { refresh, heroOverlayOpacity } = useSiteSettings();
  const [overlay, setOverlay] = React.useState(typeof heroOverlayOpacity === 'number' ? heroOverlayOpacity : 50);
  const [overlaySaving, setOverlaySaving] = React.useState(false);

  React.useEffect(() => {
    if (typeof heroOverlayOpacity === 'number') setOverlay(heroOverlayOpacity);
  }, [heroOverlayOpacity]);

  const pick = async (key) => {
    setSaving(key);
    // Apply instantly so the admin sees the change while saving
    document.documentElement.setAttribute('data-theme', key);
    try {
      await axios.put(
        `${API}/admin/site-settings`,
        { theme: key },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true },
      );
      toast.success('Thème appliqué');
      if (onSaved) onSaved(key);
      refresh();
    } catch (err) {
      // Revert on failure
      document.documentElement.setAttribute('data-theme', currentTheme || 'default');
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const saveOverlay = async () => {
    setOverlaySaving(true);
    try {
      await axios.put(
        `${API}/admin/site-settings`,
        { hero_overlay_opacity: overlay },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true },
      );
      toast.success('Voile du héros enregistré');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setOverlaySaving(false);
    }
  };

  return (
    <div
      data-testid="theme-picker-card"
      className="bg-white border border-[#E2E2DF] rounded-lg p-5"
    >
      <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-1 flex items-center gap-2">
        🎨 Thème de couleurs du site
      </h3>
      <p className="text-xs text-[#737373] mb-4 leading-relaxed">
        Choisissez la palette. Le changement s'applique <strong>immédiatement</strong> sur
        toutes les pages du site (visiteurs inclus, à leur prochain chargement). Vous pouvez
        revenir à l'original à tout moment.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {THEMES.map((t) => {
          const isActive = (currentTheme || 'default') === t.key;
          const isSaving = saving === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => !isActive && pick(t.key)}
              disabled={isActive || isSaving}
              data-testid={`theme-picker-${t.key}`}
              className={`group text-left p-3 rounded-md border-2 transition-all ${isActive ? 'border-[#1A3C34] bg-[#F0EFEA]' : 'border-[#E2E2DF] hover:border-[#1A3C34] bg-white'}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                {t.swatches.map((c, i) => (
                  <span
                    key={i}
                    className="inline-block w-7 h-7 rounded-md border border-black/10 shadow-sm"
                    style={{ background: c }}
                  />
                ))}
                {isActive && (
                  <span className="ml-auto text-[10.5px] font-medium text-[#1A3C34] bg-white px-2 py-0.5 rounded-full border border-[#1A3C34]">
                    ✓ Actif
                  </span>
                )}
              </div>
              <div className="text-xs font-medium text-[#1A3C34]">{t.label}</div>
              {isSaving && <div className="text-[10px] text-[#737373] mt-1">Sauvegarde…</div>}
            </button>
          );
        })}
      </div>

      {/* Hero overlay opacity — controls the white scrim behind the black H1
          on the homepage. 0 = image fully visible (hard to read), 100 = pure
          white background. Default 50 = balanced. */}
      <div className="mt-6 pt-5 border-t border-[#E2E2DF]" data-testid="hero-overlay-control">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold text-[#1A3C34]">
              Voile sombre derrière le titre "Louez votre espace"
            </div>
            <div className="text-xs text-[#737373]">
              Plus la valeur est élevée, plus le titre blanc est lisible (image plus sombre).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-[#1A3C34] min-w-[44px] text-right" data-testid="hero-overlay-value">
              {overlay}%
            </span>
            <Button
              size="sm"
              onClick={saveOverlay}
              disabled={overlaySaving}
              className="h-7 text-xs"
              data-testid="hero-overlay-save"
            >
              {overlaySaving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={overlay}
          onChange={(e) => setOverlay(Number(e.target.value))}
          className="w-full h-2 rounded-lg appearance-none bg-gradient-to-r from-transparent to-white border border-[#E2E2DF] cursor-pointer"
          style={{
            background: `linear-gradient(to right, rgba(0,0,0,0.2) 0%, rgba(255,255,255,1) 100%)`,
          }}
          data-testid="hero-overlay-slider"
        />
      </div>
    </div>
  );
}


function PaymentDisclaimerCard({ value, getHeaders, onSaved }) {
  const [text, setText] = React.useState(value || '');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setText(value || ''); }, [value]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/site-settings`,
        { payment_disclaimer: text },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true },
      );
      toast.success('Message de paiement mis à jour');
      if (onSaved) onSaved(text);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const useDefault = () => setText(DISCLAIMER_DEFAULT);

  return (
    <div
      data-testid="payment-disclaimer-card"
      className="bg-white border-2 border-[#C77D63] rounded-lg p-5"
    >
      <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-1 flex items-center gap-2">
        <Info className="w-5 h-5 text-[#C77D63]" />
        Message de confirmation avant paiement
      </h3>
      <p className="text-xs text-[#737373] mb-3 leading-relaxed">
        Ce texte s'affichera dans une popup au moment où le client clique sur « Payer »
        (Stripe ou PayPal), avant d'être redirigé vers la page de paiement. Il doit
        rappeler les règles de remise en état et le supplément de 30 EUR.
      </p>
      <textarea
        data-testid="payment-disclaimer-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={9}
        placeholder={DISCLAIMER_DEFAULT}
        className="w-full text-sm font-body text-[#1A3C34] border border-[#E2E2DF] rounded-md p-3 focus:outline-none focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] whitespace-pre-line"
      />
      <p className="text-[10px] text-[#737373] mt-1">
        Astuce : laissez le champ vide pour utiliser le texte par défaut.
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button
          data-testid="save-payment-disclaimer-btn"
          size="sm"
          onClick={save}
          disabled={saving}
          className="h-9 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 text-xs"
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
        <Button
          data-testid="use-default-disclaimer-btn"
          size="sm"
          variant="outline"
          onClick={useDefault}
          className="h-9 text-xs border-[#E2E2DF] text-[#737373]"
        >
          Utiliser le texte par défaut
        </Button>
      </div>
    </div>
  );
}
